import get from 'lodash.get'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import SydAutocomplete from '../../../commonDesign/SydAutocomplete'
import SydLabel from '../../../commonDesign/SydLabel'
import { usePipelineDataContextWithVariables } from '../provider/DDPDataProvider'
import { useFilterSelectContext } from './context'
import { getFilterValue } from './getFilterValue'
import { getRestoredSurvivors } from './getRestoredSurvivors'

export const FilterSelector = ({
  label = '',
  description = '',
  filterLevel = 'account',
  pipeline,
  seriesAccessor = 'data',
  valueAccessor = 'orionEntityId',
  labelAccessor = 'levelName',
  style,
  className,
  variables,
  hideIfEmpty = false,
  size = 'sm',
  placeholder = 'Select',
  width = '100%',
  filterType,
  maxShow = 2,
  maxLabelLength = 18,
  ...selectProps
}) => {
  const { data, isLoading, isFetching } = usePipelineDataContextWithVariables(
    pipeline,
    variables
  )
  const [searchTerm, setSearchTerm] = useState('')
  const {
    selectedValues,
    setSelectedValues,
    selectedOption,
    setVariableValue,
    pendingRestore,
    consumePendingRestore
  } = useFilterSelectContext()

  const options = useMemo(() => {
    if (!data) return []
    const seriesData = get(data, seriesAccessor)
    if (!seriesData) return []
    return seriesData.map((item) => ({
      value: get(item, valueAccessor),
      label: get(item, labelAccessor) || get(item, valueAccessor)
    }))
  }, [data, labelAccessor, seriesAccessor, valueAccessor])

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options
    const lowerSearchTerm = searchTerm.toLowerCase()
    return options.filter((option) =>
      option.label.toLowerCase().includes(lowerSearchTerm)
    )
  }, [searchTerm, options])

  const setValue = useCallback(
    (val) => {
      setSelectedValues(val)
      setSearchTerm('')
      setVariableValue(
        getFilterValue(
          filterLevel,
          options.flatMap((option) => option.value),
          val.map((v) => v.value)
        )
      )
    },
    [setSelectedValues, setVariableValue, filterLevel, options]
  )

  // Wait for the pipeline to settle before applying pendingRestore. options can
  // legitimately be empty (no entities for this client); gating on options.length
  // would leave the restore unconsumed and skip self-heal of stale cached values.
  // The selectedOption check also matters: a user who switches filter types
  // before the original selector finishes loading would otherwise have the
  // hidden (now-inactive) selector write its cached values into shared context.
  useEffect(() => {
    if (!pendingRestore) return
    if (pendingRestore.filterType !== filterType) return
    if (selectedOption !== filterType) return
    if (isLoading || isFetching) return
    if (!data) return
    const survivors = getRestoredSurvivors(options, pendingRestore.selectedValues)
    setSelectedValues(survivors)
    setVariableValue(
      getFilterValue(
        filterLevel,
        options.flatMap((option) => option.value),
        survivors.map((s) => s.value)
      )
    )
    consumePendingRestore()
  }, [pendingRestore, filterType, selectedOption, options, setSelectedValues, setVariableValue, filterLevel, consumePendingRestore, isLoading, isFetching, data])

  if (filterType !== selectedOption) {
    return null
  }

  if (hideIfEmpty && !options?.length) {
    return null
  }

  return (
    <div style={{ width }}>
      <SydLabel label={label} description={description}>
        <SydAutocomplete
          onChange={(e, val) => setValue(val)}
          extraStyles={style}
          className={className}
          value={selectedValues}
          defaultValue={selectedValues}
          options={filteredOptions ?? []}
          onInputChange={(e, val) => setSearchTerm(val)}
          placeholder={placeholder}
          size={size}
          multiple
          maxShow={maxShow}
          isLoading={isLoading || isFetching}
          {...selectProps}
        />
      </SydLabel>
    </div>
  )
}

FilterSelector.propTypes = {
  label: PropTypes.string,
  description: PropTypes.string,
  applyTo: PropTypes.string,
  filterLevel: PropTypes.string,
  pipeline: PropTypes.string.isRequired,
  seriesAccessor: PropTypes.string,
  valueAccessor: PropTypes.string,
  labelAccessor: PropTypes.string,
  style: PropTypes.object,
  className: PropTypes.string,
  variables: PropTypes.object,
  hideIfEmpty: PropTypes.bool,
  size: PropTypes.oneOf(['xs', 'sm', 'lg']),
  width: PropTypes.string,
  placeholder: PropTypes.string,
  maxShow: PropTypes.number,
  maxLabelLength: PropTypes.number,
  filterType: PropTypes.string
}
